import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  FlexBox,
  Box,
  Typography,
  Button,
  SvgIconButton,
  MuiTable,
  IconButton,
  Collapse,
  MuiBox,
  Icon,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { MembershipDetailsOverlay } from 'components/MembershipDetailsOverlay';
import { Scrollbar } from 'components/Scrollbar';
import { SeatDetailsOverlay } from 'components/SeatDetailsOverlay';
import { useActions } from 'hooks/useActions';
import {
  actions as cartActions,
  selectors as cartSelectors,
  Cart,
  Item,
  MembershipDetails,
  Product
} from 'services/cart';
import {
  selectors as configSelectors,
  CartRule,
  Configuration,
  EventDisplaySettings,
  LanguageField
} from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatCartProducts } from 'utils/helpers';
import { formatCurrency, formatTitle } from 'utils/stringFormat';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import { useStyles } from './CartTable.styled';
import { CartTableProps } from './CartTable.types';

const CartTable = (props: CartTableProps) => {
  const { hasRemoveColumn, setIsOverlayOpen } = props;
  const { t, i18n } = useTranslation();

  const { removeDonation, removeTicketsFromCart } = useActions({
    removeDonation: cartActions.removeDonation,
    removeTicketsFromCart: cartActions.removeTicketsFromCart
  });

  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const formattedProducts = useMemo(() => cart && formatCartProducts(cart.products), [cart]);

  const [cartRuleViolation, setCartRuleViolation] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [membershipDetails, setMembershipDetails] = useState<MembershipDetails | null>(null);
  const [overlayType, setOverlayType] = useState<'Remove' | 'SeatDetails' | null>(null);
  const [removeProductPrompt, setRemoveProductPrompt] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`, t('cart.cartItemLabel'));
  const cartConfig: Configuration['ticketing']['cart'] = get(config, `${ticketingPath}.cart`, {});
  const expandItems = get(cartConfig, 'expandItems', false);
  const removeTickets = get(cartConfig, 'removeTickets.enabled', false);
  const cartRules: CartRule[] = get(config, `${ticketingPath}.cartRules`, []);
  const donationName = get(config, `${ticketingPath}.donations.productName.${i18n.language}`, t('donation.label'));
  const eventDisplaySettingsList: EventDisplaySettings[] = get(config, `${ticketingPath}.eventDisplaySettingsList`, []);

  const classes = useStyles();

  const removeEnabled = removeTickets && hasRemoveColumn;

  useEffect(() => {
    if (cart && expandItems) {
      const tempRows: Record<number, boolean> = {};
      formattedProducts.forEach((item, idx) => (tempRows[idx] = true));
      setExpandedRows(tempRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, expandItems]);

  useEffect(() => {
    if (setIsOverlayOpen) {
      const isOverlayOpen =
        Boolean(selectedProduct && overlayType) ||
        Boolean(selectedProduct && removeProductPrompt) ||
        Boolean(cartRuleViolation);
      setIsOverlayOpen(isOverlayOpen);
    }
  }, [selectedProduct, overlayType, removeProductPrompt, cartRuleViolation]);

  const validateCartRules = (itemsToRemove: Item[]) => {
    let isCartRuleViolated = false;
    // Validate cartRules if defined in configurations.ticketing
    if (cartRules.length > 0) {
      // Tickets can only be removed from one Product at a time (future TODO if this behavior changes)
      const product = cart.products.find(
        (prod) => prod.items && prod.items.length > 0 && prod.items.find((i) => i.id === itemsToRemove[0].id)
      );

      // Save the ids of the tickets being removed
      const itemIdsToRemove = itemsToRemove.map((item) => item.id);

      // Distinguish the removed and remaining items for the product
      const removedItems: Item[] = [];
      const remainingItems: Item[] = [];
      if (product && product.items && product.items.length > 0) {
        product.items.forEach((item) => {
          if (item && item.id) {
            if (itemIdsToRemove.includes(item.id)) {
              removedItems.push(item);
            } else {
              remainingItems.push(item);
            }
          }
        });
      }

      // Continue if the product still has remaining items
      if (remainingItems && remainingItems.length > 0) {
        // Get all price type ids that were removed, and the affected rules
        const removedTypeIds = removedItems.map((item) => item && item.typeId);
        const affectedRules = cartRules.filter(
          (rule) => rule.reliesOnIds && rule.reliesOnIds.find((reliesOnId) => removedTypeIds.includes(reliesOnId))
        );
        // Continue if removing the reliesOnId of a rule
        if (affectedRules && affectedRules.length > 0) {
          // Validate rules until one is not upheld
          affectedRules.every((affectedRule) => {
            // Ignore rules that are not configured properly
            if (
              affectedRule.dependentIds &&
              affectedRule.dependentIds.length > 0 &&
              affectedRule.reliesOnIds &&
              affectedRule.reliesOnIds.length > 0
            ) {
              // Get dependent & reliesOn items remaining
              const dependentItemsRemaining: Item[] = [];
              const reliesOnItemsRemaining: Item[] = [];
              remainingItems.forEach((item) => {
                if (item && item.typeId && affectedRule.dependentIds.includes(item.typeId)) {
                  dependentItemsRemaining.push(item);
                } else if (item && item.typeId && affectedRule.reliesOnIds.includes(item.typeId)) {
                  reliesOnItemsRemaining.push(item);
                }
              });
              // Continue if there are still dependents, but no reliesOn
              if (dependentItemsRemaining.length > 0 && reliesOnItemsRemaining.length < 1) {
                // Rule is not upheld, build error message
                const reliesOnNames =
                  product &&
                  product.items &&
                  product.items.filter((item) => affectedRule.reliesOnIds.includes(item.typeId)).map((i) => i.type);
                const uniqueNames = [...new Set(reliesOnNames)];
                let reliesOnString = uniqueNames && uniqueNames.toString();
                if (uniqueNames && uniqueNames.length > 1) {
                  const lastElement = uniqueNames.pop();
                  reliesOnString = `${uniqueNames.join(', ')} ${t('cartRule.or')} ${lastElement}`;
                }
                setCartRuleViolation(t('cartRule.violationMessage', { reliesOnString }));
                isCartRuleViolated = true;
                return false;
              }
            }
            return true;
          });
        }
      }
    }
    return isCartRuleViolated;
  };

  const onMembershipDetailsClick = (e: React.MouseEvent<HTMLButtonElement>, productIdx: number) => {
    e.stopPropagation();
    const product = cart.products[productIdx] as Product;
    setMembershipDetails(product.membershipDetails || null);
  };

  /** Remove the product and reset the state */
  const onRemoveProductConfirm = () => {
    removeTicketsFromCart([(selectedProduct as Product).removeParams]);
    onRemoveProductCancel();
  };

  /** Reset the selected product and hide the overlay */
  const onRemoveProductCancel = () => {
    setSelectedProduct(null);
    setRemoveProductPrompt(false);
  };

  /** Set the selected product and show one of the removal overlays */
  const onRemoveTicketsClick = (e: React.MouseEvent<HTMLButtonElement>, productIdx: number) => {
    e.stopPropagation();
    const product = cart.products[productIdx] as Product;
    setSelectedProduct(product);
    if (product.removeParams) {
      // Overlay prompt to remove the whole product
      setRemoveProductPrompt(true);
    } else {
      // Selection overlay to remove individual items of the product
      setOverlayType('Remove');
    }
  };

  const onRemoveTicketsConfirm = (itemsToRemove: Item[], productType: string) => {
    onSeatDetailsCancel();
    const isCartRuleViolated = validateCartRules(itemsToRemove);
    if (!isCartRuleViolated) {
      if (productType === 'donation') {
        removeDonation(itemsToRemove[0].removeParams);
      } else {
        removeTicketsFromCart(itemsToRemove.map((item) => item.removeParams));
      }
    }
  };

  const onSeatDetailsCancel = () => {
    setSelectedProduct(null);
    setOverlayType(null);
  };

  const onSeatDetailsClick = (e: React.MouseEvent<HTMLButtonElement>, productIdx: number) => {
    e.stopPropagation();
    setOverlayType('SeatDetails');
    setSelectedProduct(cart.products[productIdx] as Product);
  };

  const getOptionalPropsForDisplay = (row: Product) => {
    const { showDate, showTime, showLocation, showTier } = getEventDisplaySettings(eventDisplaySettingsList, row.name);

    const propsForDisplay = [];

    if (showDate) {
      if (row.firstDate && row.lastDate) {
        propsForDisplay.push(
          `${t('{{date, weekdayMonthDay}}', { date: new Date(row.firstDate) })} - ${t('{{date, weekdayMonthDay}}', {
            date: new Date(row.lastDate)
          })}`
        );
      } else if (row.date) {
        propsForDisplay.push(t('{{date, weekdayMonthDay}}', { date: new Date(row.date) }));
      }
    }

    if (showTime && row.date) {
      propsForDisplay.push(t('{{date, time}}', { date: new Date(row.date) }));
    }

    if (showTier && row.tier) {
      propsForDisplay.push(row.tier);
    }

    if (showLocation && row.location) {
      propsForDisplay.push(formatTitle(row.location.toLowerCase()));
    }

    return propsForDisplay;
  };

  const ItemRow = (rowProps: { item: Item; product: Product; subProduct?: Product }) => {
    const { item, product, subProduct } = rowProps;
    const productType = subProduct ? subProduct.type : product.type;
    const { showCartItemsPrice } = getEventDisplaySettings(eventDisplaySettingsList, product.name);

    return (
      <MuiTable.TableRow sx={{ display: 'flex' }}>
        <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableCollapseCell}`} />
        <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell}`}>
          <MuiBox className={classes.descriptionBox} sx={{ paddingLeft: subProduct ? '32px' : '16px' }}>
            <Typography fontSize={24} fontWeight={400}>
              {productType && productType === 'donation' ? donationName : item.type}
            </Typography>
          </MuiBox>
        </MuiTable.TableCell>
        <MuiTable.TableCell
          className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableQuantityCell} ${classes.tableNumberCell}`}
        >
          <Typography fontSize={24} fontWeight={400}>
            {item.quantity as number}
          </Typography>
        </MuiTable.TableCell>
        <MuiTable.TableCell
          className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tablePriceCell} ${classes.tableNumberCell}`}
        >
          <Typography fontSize={24} fontWeight={400}>
            {showCartItemsPrice && item.unitPrice ? formatCurrency(item.unitPrice, {}, languageSettings) : null}
          </Typography>
        </MuiTable.TableCell>
        {removeEnabled && (
          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableRemoveCell}`} />
        )}
      </MuiTable.TableRow>
    );
  };

  const Row = (rowProps: { row: Product; idx: number }) => {
    const { row, idx } = rowProps;
    const propsForDisplay = getOptionalPropsForDisplay(row);
    const isExpand = expandedRows[idx];
    const handleClick = () => {
      setExpandedRows({ ...expandedRows, [idx]: isExpand ? false : true });
    };

    return (
      <>
        <MuiTable.TableRow
          className={`${classes.tableRow} ${classes.tableBodyRow}`}
          onClick={handleClick}
          sx={
            isExpand
              ? { marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
              : { marginBottom: '20px', borderRadius: '8px' }
          }
        >
          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableCollapseCell}`}>
            <IconButton aria-label="expand row" size="small" className={classes.expandIcon}>
              {isExpand ? (
                <Icon name="KeyboardArrowUp" fontSize="large" />
              ) : (
                <Icon name="KeyboardArrowDown" fontSize="large" />
              )}
            </IconButton>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell}`}>
            <MuiBox className={classes.descriptionBox}>
              <Typography fontSize={24} fontWeight={600}>
                {row.type && row.type === 'donation' ? donationName : row.name}
              </Typography>
              <Typography fontSize={16}>
                {/* Date - Time - Tier - Location (if all enabled) */}
                {propsForDisplay.length > 0 ? propsForDisplay[0] : null}
                {propsForDisplay.length > 1 ? <> &middot; {propsForDisplay[1]} </> : null}
                {propsForDisplay.length > 2 ? <> &middot; {propsForDisplay[2]} </> : null}
                {propsForDisplay.length > 3 ? <> &middot; {propsForDisplay[3]} </> : null}
              </Typography>
            </MuiBox>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableDetailsCell}`}>
            {!row.membershipDetails && !row.isGA && (
              <Button
                onClick={(e) => onSeatDetailsClick(e, idx)}
                appearance="white"
                customSize="x-small"
                sx={{ width: 'auto', maxWidth: '200px' }}
                data-testid="seat-details-button"
              >
                {t('cart.seatDetails')}
              </Button>
            )}
            {row.membershipDetails && (
              <Button
                onClick={(e) => onMembershipDetailsClick(e, idx)}
                customSize="x-small"
                sx={{ width: 'auto', maxWidth: '200px' }}
                data-testid="membership-info-button"
              >
                {row.membershipDetails?.action === 'renewal' ? t('cart.renewalInfo') : t('cart.upgradeInfo')}
              </Button>
            )}
          </MuiTable.TableCell>
          <MuiTable.TableCell
            className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableQuantityCell} ${classes.tableNumberCell}`}
          >
            <Typography fontSize={24} fontWeight={600}>
              {row.quantity as number}
            </Typography>
          </MuiTable.TableCell>
          <MuiTable.TableCell
            className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tablePriceCell} ${classes.tableNumberCell}`}
          >
            <Typography fontSize={24} fontWeight={600}>
              {formatCurrency(row.subTotal as number, {}, languageSettings)}
            </Typography>
          </MuiTable.TableCell>
          {removeEnabled && (
            <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableRemoveCell}`}>
              <FlexBox align="center" justify="center">
                <SvgIconButton
                  data-testid="remove-tickets-button"
                  onClick={(e) => onRemoveTicketsClick(e, idx)}
                  appearance="transparent"
                  iconColor="#FFF"
                  background="rgb(224, 32, 32)"
                >
                  <SvgIcon name="exit" size={40} />
                </SvgIconButton>
              </FlexBox>
            </MuiTable.TableCell>
          )}
        </MuiTable.TableRow>

        <MuiTable.TableRow
          className={classes.tableItemsRow}
          style={isExpand ? { marginBottom: '20px' } : { display: 'none' }}
        >
          <MuiTable.TableCell className={classes.tableItemsCell}>
            <Collapse in={isExpand} timeout="auto" unmountOnExit>
              {isExpand &&
                row.subProducts &&
                row.subProducts.length > 0 &&
                row.subProducts.map((subProduct: Product, rowIdx: number) => {
                  const subProductPropsForDisplay = getOptionalPropsForDisplay(subProduct);

                  return (
                    <MuiTable.Table
                      size="small"
                      aria-label="cart-sub-table"
                      className={classes.itemsTable}
                      key={rowIdx}
                    >
                      <MuiTable.TableBody>
                        <MuiTable.TableRow sx={{ display: 'flex' }}>
                          <MuiTable.TableCell
                            className={`${classes.tableCell} ${classes.tableBodyCell} ${classes.tableCollapseCell}`}
                          />
                          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableBodyCell}`}>
                            <MuiBox className={classes.descriptionBox} sx={{ paddingLeft: '16px' }}>
                              <Typography fontSize={24} fontWeight={600}>
                                {subProduct.name}
                              </Typography>
                              <Typography fontSize={16}>
                                {/* Date - Time - Tier - Location (if all enabled) */}
                                {subProductPropsForDisplay.length > 0 ? subProductPropsForDisplay[0] : null}
                                {subProductPropsForDisplay.length > 1 ? (
                                  <> &middot; {subProductPropsForDisplay[1]} </>
                                ) : null}
                                {subProductPropsForDisplay.length > 2 ? (
                                  <> &middot; {subProductPropsForDisplay[2]} </>
                                ) : null}
                                {subProductPropsForDisplay.length > 3 ? (
                                  <> &middot; {subProductPropsForDisplay[3]} </>
                                ) : null}
                              </Typography>
                            </MuiBox>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>

                        {subProduct.items &&
                          subProduct.items.map((item: Item, itemIdx: number) => {
                            return <ItemRow key={itemIdx} item={item} product={row} subProduct={subProduct} />;
                          })}
                      </MuiTable.TableBody>
                    </MuiTable.Table>
                  );
                })}

              {isExpand && row.items && row.items.length > 0 ? (
                <MuiTable.Table size="small" aria-label="cart-sub-table" className={classes.itemsTable}>
                  <MuiTable.TableBody>
                    {row.items.map((item: Item, itemIdx: number) => {
                      return <ItemRow key={itemIdx} item={item} product={row} />;
                    })}
                  </MuiTable.TableBody>
                </MuiTable.Table>
              ) : null}
            </Collapse>
          </MuiTable.TableCell>
        </MuiTable.TableRow>
      </>
    );
  };

  return (
    <>
      <Scrollbar refreshModel={expandedRows}>
        <Box flexOverride="none" padding="0 3">
          <MuiTable.TableContainer id="cartViewTable">
            <MuiTable.Table className={classes.table} stickyHeader>
              <MuiTable.TableHead>
                <MuiTable.TableRow className={`${classes.tableRow} ${classes.tableHeadRow}`}>
                  <MuiTable.TableCell
                    className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableCollapseCell}`}
                  />
                  <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>
                    {formatTitle(cartItemLabel)}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableDetailsCell}`}
                  />
                  <MuiTable.TableCell
                    className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableQuantityCell} ${classes.tableNumberCell}`}
                  >
                    {t('commonText.quantity')}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tablePriceCell} ${classes.tableNumberCell}`}
                  >
                    {t('commonText.price')}
                  </MuiTable.TableCell>
                  {removeEnabled && (
                    <MuiTable.TableCell
                      className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableRemoveCell}`}
                    />
                  )}
                </MuiTable.TableRow>
              </MuiTable.TableHead>
              <MuiTable.TableBody>
                {formattedProducts &&
                  formattedProducts.map((row: Product, idx: number) => {
                    return <Row key={idx} idx={idx} row={row} />;
                  })}
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Box>
      </Scrollbar>

      {/* Overlay to show a cart rule violatation */}
      {cartRuleViolation && (
        <ActionOverlay
          title={cartRuleViolation}
          confirmText={t('commonText.close')}
          onConfirm={() => setCartRuleViolation('')}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Overlay to show Seat Details / Remove */}
      {selectedProduct && overlayType && (
        <SeatDetailsOverlay
          product={selectedProduct}
          view={overlayType}
          onCancel={onSeatDetailsCancel}
          onRemove={onRemoveTicketsConfirm}
        />
      )}

      {/* Overlay to confirm removing a product from the cart */}
      {removeProductPrompt && selectedProduct && (
        <ActionOverlay
          title={t('cart.removeProductOverlayHeader', { productName: selectedProduct.name })}
          cancelText={t('commonText.cancel')}
          confirmText={t('cart.yesRemove')}
          onCancel={onRemoveProductCancel}
          onConfirm={onRemoveProductConfirm}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Overlay to show the renewal or upgrade details of the membership */}
      {membershipDetails && (
        <MembershipDetailsOverlay membershipDetails={membershipDetails} onConfirm={() => setMembershipDetails(null)} />
      )}
    </>
  );
};

export default CartTable;
